import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { mdiInformationSlabCircleOutline } from '@mdi/js';
import Icon from '../Icon';
import HBox from '../layout/HBox';
import { Tooltip } from '../layout/Tooltip';
import memoStyles from './CampoTexto.module.css';
import seletorStyles from './CampoSeletor.module.css';
import ReactDOM from 'react-dom';

export type UsuarioMention = {
    id: string | number;
    nome: string; // exibido ao digitar e como label
    avatarUrl?: string;
    // Campo opcional para busca adicional
    apelido?: string;
};

type Props = {
    value?: string;
    onChange?: (value: string) => void;
    label?: string;
    placeholder?: string;
    required?: boolean;
    error?: string;
    tooltipText?: React.ReactNode;
    tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
    noBorder?: boolean;
    rows?: number;
    instanceId?: string;
    min?: number;
    max?: number;
    usuarios?: UsuarioMention[]; // lista completa dos usuários que podem ser mencionados
    carregarUsuarios?: (busca: string) => Promise<UsuarioMention[]>; // fallback async
    minCharsBusca?: number; // default 0
    /** Regex para identificar tokens de menção já existentes (para não quebrar) */
    mentionPattern?: RegExp;
    /** Delay para trigger de busca async */
    debounceMs?: number;
    [key: string]: any;
};

/**
 * CampoMemoMencao
 * Textarea semelhante ao CampoMemo que permite mencionar usuários com '@'.
 * Ao digitar '@' e pelo menos 1 char (configurável), abre um dropdown filtrável.
 */
export default function CampoMemoMencao({
    value,
    onChange,
    label,
    placeholder,
    required,
    error,
    tooltipText,
    tooltipPosition,
    noBorder,
    rows,
    instanceId,
    min,
    max,
    usuarios = [],
    carregarUsuarios,
    minCharsBusca = 1,
    mentionPattern = /@([\wÀ-ÖØ-öø-ÿ_.-]+)/g,
    debounceMs = 250,
    ...props
}: Readonly<Props>) {
    const [opcoes, setOpcoes] = useState<UsuarioMention[]>(usuarios);
    useEffect(() => { setOpcoes(usuarios); }, [usuarios]);

    // Helpers de conversão entre modelo (<@id|nome>) e visual (@nome)
    const modelToView = useCallback((s: string) => {
        if (!s) return '';
        return s.replace(/<@([^|>]+)\|([^>]+)>/g, '@$2');
    }, []);

    const displayToModel = useCallback((s: string) => {
        if (!s) return '';
        let out = s;
        // Ordena por nome mais longo primeiro para evitar substituições parciais
        const users = [...opcoes].sort((a, b) => (b.nome?.length ?? 0) - (a.nome?.length ?? 0));
        for (const u of users) {
            const pattern = new RegExp(`(^|[^<])@${u.nome.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')}`, 'g');
            out = out.replace(pattern, (m, p1) => `${p1}<@${u.id}|${u.nome}>`);
        }
        return out;
    }, [opcoes]);

    const [local, setLocal] = useState(modelToView(value ?? ''));
    const [anchorPos, setAnchorPos] = useState<{ top: number; left: number } | null>(null);
    const [open, setOpen] = useState(false);
    const [busca, setBusca] = useState('');
    const [highlightIdx, setHighlightIdx] = useState(0);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const loadingRef = useRef(false);
    const debounceRef = useRef<number | undefined>(undefined);

    // Ajusta rows dinamicamente (similar ao CampoMemo)
    const computedRows = useMemo(() => rows ?? (local?.split('\n')?.length || 1) + 1, [rows, local]);

    // Atualiza local quando value externo muda
    useEffect(() => { setLocal(modelToView(value ?? '')); }, [value, modelToView]);

    // Filtra opções locais
    const filtered = useMemo(() => {
        if (!busca) return [];
        const b = busca.toLowerCase();
        return opcoes.filter(u => u.nome.toLowerCase().includes(b) || u.apelido?.toLowerCase().includes(b));
    }, [busca, opcoes]);

    const efetuarBuscaRemota = useCallback(async (term: string) => {
        if (!carregarUsuarios || term.length < minCharsBusca) return;
        loadingRef.current = true;
        try {
            const res = await carregarUsuarios(term);
            setOpcoes(res);
        } finally {
            loadingRef.current = false;
        }
    }, [carregarUsuarios, minCharsBusca]);

    // Debounce busca remota
    useEffect(() => {
        if (!carregarUsuarios) return;
        if (debounceRef.current) window.clearTimeout(debounceRef.current);
        if (busca.length >= minCharsBusca) {
            debounceRef.current = window.setTimeout(() => efetuarBuscaRemota(busca), debounceMs);
        }
        return () => { if (debounceRef.current) window.clearTimeout(debounceRef.current); };
    }, [busca, debounceMs, efetuarBuscaRemota, carregarUsuarios, minCharsBusca]);

    const closeDropdown = () => {
        setOpen(false);
        setBusca('');
        setHighlightIdx(0);
        setAnchorPos(null);
    };

    const inserirMencao = (usuario: UsuarioMention) => {
        const ta = textareaRef.current;
        if (!ta) return;
        const start = ta.selectionStart ?? 0;
        const before = local.slice(0, start);
        // Busca última ocorrência de '@' que iniciou a menção
        const atIdx = before.lastIndexOf('@');
        if (atIdx === -1) return; // fallback
        const prefix = before.slice(0, atIdx);
        const mentionTokenDisplay = `@${usuario.nome}`;
        const mentionTokenModel = `<@${usuario.id}|${usuario.nome}>`;
        const afterOriginal = local.slice(start);
        const novoDisplay = prefix + mentionTokenDisplay + ' ' + afterOriginal; // adiciona espaço após menção
        setLocal(novoDisplay);
        // Converte display inteiro em modelo, garantindo que a menção recém inserida vire token
        const novoModelo = displayToModel(novoDisplay).replace(mentionTokenDisplay, mentionTokenModel);
        onChange?.(novoModelo);
        // Ajusta caret após menção + espaço
        requestAnimationFrame(() => {
            const pos = prefix.length + mentionTokenDisplay.length + 1;
            ta.selectionStart = ta.selectionEnd = pos;
            ta.focus();
        });
        closeDropdown();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        // Se o dropdown está aberto, trata navegação interna
        if (open) {
            switch (e.code.toLowerCase()) {
                case 'arrowdown':
                    e.preventDefault();
                    setHighlightIdx(i => (i + 1) % (filtered.length || 1));
                    return;
                case 'arrowup':
                    e.preventDefault();
                    setHighlightIdx(i => (i - 1 + (filtered.length || 1)) % (filtered.length || 1));
                    return;
                case 'enter':
                    if (filtered[highlightIdx]) {
                        e.preventDefault();
                        inserirMencao(filtered[highlightIdx]);
                    }
                    return;
                case 'escape':
                    e.preventDefault();
                    closeDropdown();
                    return;
            }
        }
        // Propaga para o handler externo se existir
        props.onKeyDown?.(e);
    };

    const handleChange = (val: string) => {
        setLocal(val);
        // Em digitação livre, convertemos @nome -> <@id|nome> quando houver correspondência
        onChange?.(displayToModel(val));
        const ta = textareaRef.current;
        if (!ta) return;
        const caret = ta.selectionStart ?? 0;
        const before = val.slice(0, caret);
        // Identifica se estamos dentro de um token de menção em digitação
        const atIdx = before.lastIndexOf('@');
        if (atIdx >= 0) {
            // Verifica se há espaço/linha antes ou é início do texto
            const charBefore = atIdx === 0 ? ' ' : before[atIdx - 1];
            if (/\s|[([{}.,;!?]/.test(charBefore) || atIdx === 0) {
                const query = before.slice(atIdx + 1);
                if (/^[\wÀ-ÖØ-öø-ÿ_.-]*$/.test(query)) {
                    // Atualiza busca e posição
                    setBusca(query);
                    // Calcula posição do caret para posicionar dropdown
                    positionDropdown(atIdx);
                    setOpen(query.length >= minCharsBusca);
                    return;
                }
            }
        }
        // Se saiu das condições, fecha
        if (open) closeDropdown();
    };

    const positionDropdown = (atIdx: number) => {
        const ta = textareaRef.current;
        if (!ta) return;
        // Cria um espelho oculto para medir posição aproximada
        const div = document.createElement('div');
        const style = window.getComputedStyle(ta);
        // posiciona o shadow no mesmo local do textarea (viewport), para obter retângulos corretos
        div.style.position = 'fixed';
        div.style.visibility = 'hidden';
        div.style.whiteSpace = 'pre-wrap';
        // Quebras de linha similares ao textarea
        (div.style as any).wordWrap = 'break-word';
        div.style.font = style.font;
        div.style.padding = style.padding;
        div.style.border = style.border;
        div.style.width = ta.offsetWidth + 'px';
        div.style.lineHeight = style.lineHeight;
        const taRect = ta.getBoundingClientRect();
        div.style.left = taRect.left + 'px';
        div.style.top = taRect.top + 'px';
        div.textContent = ta.value.slice(0, atIdx);
        const marker = document.createElement('span');
        marker.textContent = '@';
        div.appendChild(marker);
        document.body.appendChild(div);
        const rect = marker.getBoundingClientRect();
        // coordenadas absolutas da página
        setAnchorPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
        document.body.removeChild(div);
    };

    useEffect(() => {
        const clickHandler = (e: MouseEvent) => {
            if (
                dropdownRef.current &&
                e.target instanceof HTMLElement &&
                (dropdownRef.current.contains(e.target) || textareaRef.current?.contains(e.target))
            ) return;
            closeDropdown();
        };
        document.addEventListener('click', clickHandler);
        return () => document.removeEventListener('click', clickHandler);
    }, []);

    return (
        <div style={{ width: '100%', position: 'relative' }}>
            <label>
                <HBox stretch>
                    <span>{label}</span>
                    {tooltipText && (
                        <Tooltip text={tooltipText} position={tooltipPosition}>
                            <Icon
                                path={mdiInformationSlabCircleOutline}
                                size={0.8}
                                color="var(--tc-color-info)"
                            />
                        </Tooltip>
                    )}
                </HBox>
            </label>
            <textarea
                {...props}
                ref={textareaRef}
                id={instanceId}
                className={[memoStyles.container, error ? memoStyles.error : null].join(' ')}
                style={{
                    padding: '4px',
                    width: '100%',
                    border: noBorder ? 'none' : undefined,
                    position: 'relative'
                }}
                rows={computedRows}
                value={local}
                onChange={e => handleChange(e.target.value)}
                placeholder={placeholder}
                required={required}
                onKeyDown={handleKeyDown}
            />
            {(max || error) && (
                <HBox stretch>
                    <div className={memoStyles.error}>{error}</div>
                    <div className={[
                        memoStyles.counter,
                        max && (local.length ?? 0) > max ? memoStyles.error : null,
                        min && (local.length ?? 0) < min ? memoStyles.error : null,
                    ].join(' ')}>
                        {(local.length ?? 0)}/{max}
                    </div>
                </HBox>
            )}
            {open && anchorPos && (
                ReactDOM.createPortal(
                    <div
                        ref={dropdownRef}
                        className={[seletorStyles.options, seletorStyles.optionsDown].join(' ')}
                        style={{
                            top: anchorPos.top + 4,
                            left: anchorPos.left + 12, /* compensa margin negativa do CSS */
                            position: 'absolute',
                            minWidth: 200,
                            maxHeight: 240,
                            overflowY: 'auto'
                        }}
                    >
                        {filtered.length === 0 && (
                            <div className={seletorStyles.noResults}>
                                {loadingRef.current ? 'Carregando...' : '(Nenhum resultado)'}
                            </div>
                        )}
                        {filtered.map((u, idx) => (
                            <div
                                key={u.id}
                                className={[
                                    seletorStyles.option,
                                    idx === highlightIdx ? seletorStyles.selected : null
                                ].join(' ')}
                                onMouseEnter={() => setHighlightIdx(idx)}
                                onMouseDown={(e) => { e.preventDefault(); inserirMencao(u); }}
                            >
                                <HBox gap="8px" style={{ alignItems: 'center' }}>
                                    {u.avatarUrl && (
                                        // eslint-disable-next-line @next/next/no-img-element
                                        <img src={u.avatarUrl} alt={u.nome} style={{ width: 24, height: 24, borderRadius: '50%' }} />
                                    )}
                                    <span>{u.nome}</span>
                                    {u.apelido && <span style={{ color: 'var(--tc-color-gray-500)', fontSize: 12 }}>({u.apelido})</span>}
                                </HBox>
                            </div>
                        ))}
                    </div>,
                    document.querySelector('#root')!
                )
            )}
        </div>
    );
}
